import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams();
  const movieId = parseInt(id || '1', 10);
  const movie = movies.find((m: Movie) => m.id === movieId) || movies[0];
  const category = categories.find((c) => c.slug === movie.categorySlug);
  const relatedMovies = movies
    .filter((m: Movie) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 max-w-7xl mx-auto">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                首页
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {category?.name || movie.category}
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <span className="text-foreground-900 font-medium">{movie.title}</span>
            </li>
          </ol>
        </nav>

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full md:w-[300px]">
                <div className="overflow-hidden rounded-2xl aspect-[3/4]">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    title={`${movie.title} - 丫丫影院`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-400 mb-5">{movie.originalTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="px-3 py-1.5 rounded-lg bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap">
                    {movie.rating}
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-sm font-medium whitespace-nowrap">
                    {movie.category}
                  </span>
                  {movie.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-sm font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6 p-5 rounded-xl bg-background-100">
                  <div>
                    <p className="text-xs text-foreground-400">年份</p>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{movie.year}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">地区</p>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{movie.region}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">时长</p>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{movie.duration}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">导演</p>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{movie.director}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-foreground-400">主演</p>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{movie.actors.join(' / ')}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-8 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="px-8 py-3 rounded-xl bg-secondary-100 text-secondary-700 font-semibold text-sm hover:bg-secondary-200 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-14 md:mt-20">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {relatedMovies.map((rm: Movie) => (
                    <Link
                      key={rm.id}
                      to={`/article/${rm.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4]">
                        <img
                          src={rm.coverImage}
                          alt={rm.title}
                          title={`${rm.title} - 丫丫影院`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                            {rm.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{rm.year}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-14 md:mt-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                常见问题
              </h2>
              <div className="space-y-3 max-w-3xl">
                <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-sm font-medium text-foreground-900">
                    如何在丫丫影院免费观看{movie.title}？
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-lg"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    您可以在丫丫影院官网首页或分类页面找到{movie.title}，点击海报即可进入详情页，选择"立即播放"按钮即可在线观看。无需注册，完全免费。
                  </p>
                </details>
                <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-sm font-medium text-foreground-900">
                    {movie.title}支持高清画质吗？
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-lg"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    是的，丫丫影院所有影视资源均提供高清画质选项，{movie.title}已更新至超清版本，确保您获得最佳的观影体验。播放流畅不缓冲，画质清晰细腻。
                  </p>
                </details>
                <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-sm font-medium text-foreground-900">
                    丫丫影院是否需要下载App才能观看？
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-lg"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    不需要。丫丫影院是纯网页端在线观看平台，您可以直接通过浏览器访问官网，无需下载任何App或插件，随时随地畅享精彩影视内容。
                  </p>
                </details>
              </div>
            </section>
          </div>
        </article>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yayacinema.com/' },
              { '@type': 'ListItem', position: 2, name: movie.category, item: 'https://www.yayacinema.com/category/' + movie.categorySlug },
              { '@type': 'ListItem', position: 3, name: movie.title, item: 'https://www.yayacinema.com/article/' + movie.id },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${movie.title} - 丫丫影院免费在线观看`,
            "description": movie.description,
            "author": {
              "@type": "Organization",
              "name": "丫丫影院"
            },
            "publisher": {
              "@type": "Organization",
              "name": "丫丫影院"
            },
            "datePublished": `${movie.year}-01-01`,
            "image": movie.coverImage
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `如何在丫丫影院免费观看${movie.title}？`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `您可以在丫丫影院官网首页或分类页面找到${movie.title}，点击海报即可进入详情页，选择"立即播放"按钮即可在线观看。无需注册，完全免费。`
                }
              },
              {
                "@type": "Question",
                "name": `${movie.title}支持高清画质吗？`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `是的，丫丫影院所有影视资源均提供高清画质选项，${movie.title}已更新至超清版本，确保您获得最佳的观影体验。`
                }
              },
              {
                "@type": "Question",
                "name": "丫丫影院是否需要下载App才能观看？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "不需要。丫丫影院是纯网页端在线观看平台，您可以直接通过浏览器访问官网，无需下载任何App或插件。"
                }
              }
            ]
          })}
        </script>
      </main>
      <Footer />
    </>
  );
}